import { createFileRoute } from "@tanstack/react-router";
import {
  LiveChart,
  LiveRatesTable,
  HindiWeightCalc,
  InHiLinkMatrix,
  FaqAccordion,
  KaratComparisonTable,
  buildInHiSchema,
  HindiUniqueBlock,
  hiDateParts,
  goldRatesQueryOptions,
} from "@/components/in-hi/shell";

const SLUG = "/in/hi/gold-rate-22k-24k";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "आज 22 कैरेट और 24 कैरेट सोने का भाव क्या है?", a: "भारत के सराफा बाजार में आज 22 कैरेट और 24 कैरेट सोने का भाव लाइव अपडेट कर दिया गया है। आप ऊपर दी गई तालिका में अपने शहर के अनुसार सटीक दाम देख सकते हैं।" },
  { q: "शुद्ध 24 कैरेट सोने का आज का रेट प्रति 10 ग्राम क्या है?", a: "शुद्ध 24 कैरेट सोने का आज का दाम प्रति 10 ग्राम की सटीक कीमत के साथ ऊपर 'लाइव भाव तालिका' में दिया गया है। यह अंतरराष्ट्रीय स्पॉट × USD/INR × 10.4 (कस्टम + GST) फॉर्मूला से निकाला जाता है।" },
  { q: "सोने का भाव आने वाले दिनों में बढ़ेगा या घटेगा?", a: "सोने का भाव अगले कुछ दिनों में मुख्य रूप से डॉलर इंडेक्स (DXY), अमेरिकी बॉन्ड यील्ड, RBI द्वारा खरीदे गए रिजर्व और भू-राजनीतिक तनाव पर निर्भर करेगा। अभी का 30-दिन का ट्रेंड ऊपर ग्राफ में दिख रहा है।" },
];

export const Route = createFileRoute("/in/hi/gold-rate-22k-24k")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const dp = hiDateParts();
    const title = `आज सोने का रेट 22 कैरेट और 24 कैरेट — प्रति 10 ग्राम लाइव भाव`;
    const description = `22 कैरेट और 24 कैरेट सोने का आज का रेट भारत में — प्रति ग्राम, 10 ग्राम और तोला। मेकिंग चार्ज, GST और शुद्ध बुलियन के बीच अंतर के साथ लाइव अपडेट।`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "hi-IN" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "hi_IN" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "hi-IN", href: CANONICAL },
      ],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(buildInHiSchema({ slug: SLUG, name: title, description, faqs: FAQS, breadcrumbLabel: "22K बनाम 24K कैरेट भाव" })) }],
    };
  },
  component: Page,
});

function Page() {
  const dp = hiDateParts();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">भारत · कैरेट-विशेष</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          आज सोने का रेट 22 कैरेट और 24 कैरेट — प्रति 10 ग्राम लाइव भाव ({dp.dayNum} {dp.month} {dp.year})
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          यहां <strong>22K आभूषण सोना</strong> और <strong>24K निवेश शुद्ध सोना</strong> दोनों का लाइव भाव प्रति ग्राम/10 ग्राम में दिया गया है। पूरा भारतीय बुलियन ट्रेंड और मेकिंग-चार्ज तुलना भी देखें। मुख्य <a href="/gold-rate-india" className="text-amber-700 dark:text-amber-300 underline">India gold rate hub</a> पर अंग्रेजी संस्करण उपलब्ध है।
        </p>
      </header>
      <HindiUniqueBlock slug={SLUG} />
      <LiveChart title={`22K vs 24K मूल्य ट्रेंड — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <HindiWeightCalc />
      <KaratComparisonTable />
      <FaqAccordion items={FAQS} />
      <InHiLinkMatrix current={SLUG} />
    </main>
  );
}